import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Form } from '@/components/ui/form';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useSearchParams } from 'react-router-dom';
import {
  useAddTeamMembers,
  useGetCompanyData,
  useGetRoles,
  useUpdateTeamMemberRoles,
} from '@/hooks/use-company';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { toast } from '@/hooks/use-toast';
import { TeamMemberFormCard, TeamMemberFormContainer } from '@/components/ui/team-member-form';
import { AdministratorCard } from '@/components/ui/administrator-card';
import { RolePermissionsCard } from '@/components/ui/role-permissions-card';
import { ContactInfoCard } from '@/components/ui/contact-info-card';

// Form validation schema
const teamMemberSchema = z.object({
  id: z.string().optional(), // Add ID field to track existing members
  fullName: z.string().optional(),
  email: z.string().optional(),
  roleIds: z.array(z.string()).optional().default([]),
}).refine((data) => {
  // If any field is filled, then all required fields must be filled
  const hasAnyData = data.fullName || data.email || (data.roleIds && data.roleIds.length > 0);

  if (!hasAnyData) return true; // If no data, it's valid (optional)

  // If has any data, validate all required fields
  const isValid =
    data.fullName && data.fullName.trim().length > 0 &&
    data.email && data.email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) &&
    data.roleIds && data.roleIds.length > 0;

  return isValid;
}, {
  message: "If adding a team member, full name, valid email, and at least one role are required",
  path: ["fullName"], // This will show the error on the fullName field
});

const formSchema = z.object({
  teamMembers: z.array(teamMemberSchema).optional().default([]),
});

type FormValues = z.infer<typeof formSchema>;

export default function Step2TeamInformation() {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('orgId');

  // State to track original team members for detecting removals
  const [originalTeamMembers, setOriginalTeamMembers] = useState<
    Array<{ id: string; fullName: string; email: string; roleIds: string[] }>
  >([]);

  // API hooks
  const addTeamMembers = useAddTeamMembers();
  const updateTeamMemberRoles = useUpdateTeamMemberRoles();
  const { handleError } = useErrorHandler();

  // Fetch existing team members data
  const { data: companyData, isLoading: isLoadingTeamMembers } =
    useGetCompanyData(organizationId, 'teamMembers');

  // Fetch roles data
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRoles();

  // Form setup with optional team members
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      teamMembers: [{ id: undefined, fullName: '', email: '', roleIds: [] }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'teamMembers',
  });

  // Prefill form with existing team members
  useEffect(() => {
    if (companyData?.teamMembers && companyData.teamMembers.length > 0) {
      console.log(
        'Prefilling form with existing team members:',
        companyData.teamMembers
      );

      // Map the existing team members to form format
      const existingMembers = companyData.teamMembers.map((member) => ({
        id: member.id,
        fullName: member.fullName,
        email: member.email,
        roleIds: member.roles.map((userRole) => userRole.role.id),
      }));

      console.log('Mapped team members for form:', existingMembers);

      // Store original team members for detecting removals
      setOriginalTeamMembers(existingMembers);

      // Reset form with existing data
      form.reset({
        teamMembers: existingMembers,
      });
    } else if (
      companyData &&
      (!companyData.teamMembers || companyData.teamMembers.length === 0)
    ) {
      // No existing team members, initialize with empty array

      setOriginalTeamMembers([]);
      form.reset({
        teamMembers: [{ id: undefined, fullName: '', email: '', roleIds: [] }],
      });
    }
  }, [companyData, form]);

  const handleNext = async (data: FormValues) => {
    console.log('Step 2 data:', data);
    console.log('Organization ID:', organizationId);

    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID is missing. Please try again.',
        variant: 'destructive',
      });
      return false;
    }

    // If no team members are provided, just proceed to next step
    if (!data.teamMembers || data.teamMembers.length === 0) {
      console.log('No team members to add, proceeding to next step');
      toast({
        title: 'Step Completed',
        description: 'Proceeding to next step. You can add team members later.',
        variant: 'success',
      });
      return true;
    }

    // Filter out empty team members (those with no name and email)
    const validTeamMembers = data.teamMembers.filter(
      member => member.fullName?.trim() && member.email?.trim()
    );

    if (validTeamMembers.length === 0) {
      console.log('No valid team members to add, proceeding to next step');
      toast({
        title: 'Step Completed',
        description: 'Proceeding to next step. You can add team members later.',
        variant: 'success',
      });
      return true;
    }

    try {
      // Check if this is the first time (no existing team members)
      const isFirstTime = !originalTeamMembers.length;

      if (isFirstTime) {
        // First time: only add new team members
        const payload = {
          members: validTeamMembers.map((member) => ({
            fullName: member.fullName!,
            email: member.email!,
            roleIds: member.roleIds,
          })),
        };

        const result = await addTeamMembers.mutateAsync({
          companyId: organizationId,
          data: payload,
        });

        console.log('Added new team members (first time):', result);

        toast({
          title: 'Team Members Added',
          description: `${validTeamMembers.length} team member(s) added successfully.`,
          variant: 'success',
        });
        return true;
      } else {
        // Existing data: handle updates, additions, and removals
        await handleExistingTeamMembersUpdate({ teamMembers: validTeamMembers });
        return true;
      }
    } catch (error: any) {
      console.error('Updating team members failed:', error);
      handleError(error, {
        customMessage: 'Failed to update team members. Please try again.',
      });
      return false;
    }
  };

  const handleExistingTeamMembersUpdate = async (data: FormValues) => {
    // Ensure we have team members array
    const teamMembers = data.teamMembers || [];

    // Separate new members and existing members
    const newMembers = teamMembers.filter((member) => !member.id);
    const existingMembers = teamMembers.filter((member) => member.id);

    // Find removed members by comparing original list with current form data
    const currentMemberIds = existingMembers.map((member) => member.id);
    const removedMembers = originalTeamMembers.filter(
      (originalMember) => !currentMemberIds.includes(originalMember.id)
    );

    console.log('New members:', newMembers);
    console.log('Existing members:', existingMembers);
    console.log('Removed members:', removedMembers);

    // Create array of all API promises
    const apiPromises = [];

    // Update removed team members with empty roleIds array
    if (removedMembers.length > 0) {
      const removePromises = removedMembers.map((member) =>
        updateTeamMemberRoles.mutateAsync({
          userId: member.id,
          roleIds: [], // Empty array to remove all roles
        })
      );
      apiPromises.push(...removePromises);
    }

    // Update existing team members' roles
    if (existingMembers.length > 0) {
      const updatePromises = existingMembers.map((member) =>
        updateTeamMemberRoles.mutateAsync({
          userId: member.id!,
          roleIds: member.roleIds,
        })
      );
      apiPromises.push(...updatePromises);
    }

    // Add new team members
    if (newMembers.length > 0) {
      const payload = {
        members: newMembers.map((member) => ({
          fullName: member.fullName!,
          email: member.email!,
          roleIds: member.roleIds,
        })),
      };

      const addPromise = addTeamMembers.mutateAsync({
        companyId: organizationId!,
        data: payload,
      });
      apiPromises.push(addPromise);
    }

    // Execute all API calls in parallel
    await Promise.all(apiPromises);

    // Generate success message
    const message = generateSuccessMessage(
      existingMembers.length,
      newMembers.length,
      removedMembers.length
    );

    toast({
      title: 'Team Members Updated',
      description: message,
      variant: 'success',
    });

    console.log('Team members updated successfully');
  };

  const generateSuccessMessage = (
    updated: number,
    added: number,
    removed: number
  ): string => {
    const actions = [];
    if (updated > 0) actions.push(`${updated} member(s) updated`);
    if (added > 0) actions.push(`${added} new member(s) added`);
    if (removed > 0) actions.push(`${removed} member(s) removed`);

    return `${actions.join(', ')} successfully.`;
  };

  // Derived state for better performance
  const isLoading = isLoadingTeamMembers || isLoadingRoles;

  // Optimized role options memo
  const roleOptions = useMemo(
    () =>
      rolesData?.data?.map((role) => ({
        value: role.id,
        label: role.name,
      })) || [],
    [rolesData]
  );

  // Optimized remove team member function
  const removeTeamMember = useCallback(
    (index: number) => {
      remove(index);
    },
    []
  );

  // Optimized add team member function
  const addTeamMember = useCallback(() => {
    append({ id: undefined, fullName: '', email: '', roleIds: [] });
  }, [append]);

  // Check for missing organizationId
  useEffect(() => {
    if (!organizationId) {
      toast({
        title: 'Missing Organization ID',
        description: 'Please complete the signup process first.',
        variant: 'warning',
      });
    }
  }, [organizationId]);

  // Debug: Log company data when it changes
  useEffect(() => {
    if (companyData) {
      console.log('Company data fetched:', companyData);
    }
  }, [companyData]);

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={5}
      onNext={async () => {
        const isValid = await form.trigger();
        if (!isValid) return false;

        // Wrap the form submission to ensure it always returns a boolean
        try {
          // Create a promise that resolves to the result of handleNext
          return new Promise((resolve) => {
            form.handleSubmit(async (data) => {
              const result = await handleNext(data);
              resolve(result !== false); // If handleNext returns false, return false; otherwise true
            })();
          });
        } catch (error) {
          console.error('Form submission error:', error);
          return false;
        }
      }}
      isLoading={addTeamMembers.isPending || updateTeamMemberRoles.isPending}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
            Add Team Members
          </h2>
          <p className="text-muted-foreground">
            Invite your team to join and assign roles so everyone can
            collaborate and manage projects efficiently.
          </p>
        </div>

        <Form {...form}>
          <div className="space-y-4">
            {/* Show loading state while fetching data */}
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                <span className="ml-2 text-muted-foreground">
                  Loading{' '}
                  {isLoadingTeamMembers ? 'existing team members' : 'roles'}...
                </span>
              </div>
            )}

            {/* Company Administrator Section */}
            <AdministratorCard
              fullName={companyData?.organization?.name}
              email={companyData?.organization?.businessEmail}
              role="Admin"
              isLoading={isLoading}
            />

            {/* Add Team Members Section */}
            <ContactInfoCard title="Add Team Members">
              <TeamMemberFormContainer
                mode="simple"
                onAddMember={addTeamMember}
                addButtonText="Add Another Team Member"
                addButtonStyle="center"
              >
                {fields.map((field, index) => (
                  <TeamMemberFormCard
                    key={field.id}
                    mode="simple"
                    index={index}
                    fieldId={field.id}
                    canRemove={fields.length > 1}
                    onRemove={() => removeTeamMember(index)}
                    simpleProps={{
                      control: form.control,
                      roleOptions,
                      isLoadingRoles
                    }}
                  />
                ))}
              </TeamMemberFormContainer>
            </ContactInfoCard>

            {/* User Role Permissions Section */}
            <RolePermissionsCard />
          </div>
        </Form>
      </div>
    </OnboardingLayout>
  );
}
